import React, { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Segmented } from '@/components/ds';
import { Bars, Dots, lineSeries, quietAxis, quietGrid } from '@/components/viz';
import { useAdminInfraInsights } from '@/hooks/admin/useAdminInfraInsights';
import {
  AdminInfraInsightsPosture,
  AdminInfraInsightsServiceSpend,
  AdminInfraInsightsWatchItem,
} from '@/services/api/admin';

// ─── Design tokens ─────────────────────────────────────────────────────────
// Deliberately restrained: lime is the one brand accent, cyan/amber carry
// meaning (informational / caution), red is reserved for real danger states.
// Everything else — categories, ranks, secondary detail — stays in the
// neutral gray scale rather than reaching for a new hue per data series.
const C = {
  card: 'var(--tx-glass-panel)',
  border: 'var(--tx-line)',
  text: 'var(--tx-text)',
  muted: 'var(--tx-muted)',
  subtle: 'var(--tx-faint)',
  lime: '#c0ff02',
  blue: '#5AE9FF',
  amber: '#FFF981',
  red: '#DB5282',
  monitor: '#9A938A',
};
// Ink for the status strip, which sits on the page ground rather than inside a sheet; the ground is dark in Night,
// so the plain text tokens are the ones that read in both modes
const GROUND_INK = 'var(--tx-text)';
const GROUND_MUTED = 'var(--tx-muted)';
const font = "'Manrope', sans-serif";

const tabs = ['Overview', 'Costs', 'Usage', 'Unit Economics', 'Health', 'Signals'] as const;
type Tab = (typeof tabs)[number];
// Sentence-case labels for the tab control; the values stay as-is.
const tabOptions: [Tab, string][] = tabs.map((tab) => [
  tab,
  tab === 'Unit Economics' ? 'Unit economics' : tab,
]);

const serviceCategories = [
  {
    label: 'App runtime',
    keywords: ['Lambda', 'App Runner', 'EC2', 'Elastic Compute', 'Elastic Load Balancing'],
  },
  { label: 'Data layer', keywords: ['DynamoDB', 'RDS', 'Aurora', 'Glue'] },
  {
    label: 'Network & delivery',
    keywords: ['VPC', 'CloudFront', 'Route 53', 'API Gateway', 'NAT Gateway'],
  },
  { label: 'Storage', keywords: ['S3', 'Elastic File System', 'EFS'] },
  {
    label: 'AI / processing',
    keywords: ['Textract', 'Bedrock', 'Comprehend', 'Step Functions', 'States'],
  },
  { label: 'Auth & comms', keywords: ['Cognito', 'SES', 'SNS', 'SQS', 'Pinpoint'] },
  { label: 'AI / processing', keywords: ['Claude'] },
  { label: 'Maps & vendor discovery', keywords: ['Google Maps', 'Places', 'Geocoding'] },
];

const storyAccents = [C.lime, C.blue, C.amber];

// Neutral ink for series marks (the viz system's bar ink), and the data-coverage sources in the order the
// Data coverage caption names them
const SERIES_INK = 'rgba(var(--tx-ink-rgb), 0.28)';
const PRIOR_INK = 'rgba(var(--tx-ink-rgb), 0.14)';
const coverageLabels: Record<string, string> = {
  costExplorer: 'AWS',
  ses: 'SES',
  cognito: 'Cognito',
  anthropic: 'Claude',
  google: 'Google',
};

// A time series marks only its latest point (the current period) in lime; every other point stays bare
const nowDot =
  (lastIndex: number) =>
  ({ cx, cy, index }: { cx?: number; cy?: number; index?: number }) =>
    index === lastIndex && cx != null && cy != null ? (
      <circle key="now" cx={cx} cy={cy} r={3.5} fill={C.lime} stroke="none" />
    ) : (
      <g key={`pt-${index}`} />
    );

const truncateAxisLabel = (value: string, max = 24) =>
  value.length > max ? `${value.slice(0, max - 1)}…` : value;

const ServiceAxisTick = ({ y = 0, payload }: any) => (
  <text x={0} y={y} dy={4} textAnchor="start" fill={C.muted} fontFamily={font} fontSize={10}>
    {truncateAxisLabel(String(payload?.value ?? ''), 27)}
  </text>
);

const fallback = {
  generatedAt: '',
  account: { id: null as string | null, arn: null as string | null, region: 'us-west-2' },
  spend: {
    mtd: 0,
    previousMonth: 0,
    monthDeltaPct: null as number | null,
    sevenDay: 0,
    sevenDayDeltaPct: null as number | null,
    monthly: [] as { month: string; spend: number }[],
    daily: [] as { date?: string; spend: number }[],
    topServices: [] as AdminInfraInsightsServiceSpend[],
  },
  posture: [] as AdminInfraInsightsPosture[],
  watchlist: [] as AdminInfraInsightsWatchItem[],
  claudeSpend: {
    mtd: 0,
    previousMonth: 0,
    monthDeltaPct: null as number | null,
    monthly: [] as { month: string; spend: number }[],
    daily: [] as { date?: string; spend: number }[],
    topModels: [] as AdminInfraInsightsServiceSpend[],
  },
  googleSpend: {
    mtd: 0,
    previousMonth: 0,
    monthDeltaPct: null as number | null,
    monthly: [] as { month: string; spend: number }[],
    daily: [] as { date?: string; spend: number }[],
    topSkus: [] as AdminInfraInsightsServiceSpend[],
  },
  rawAvailability: {
    costExplorer: false,
    ses: false,
    cognito: false,
    anthropic: false,
    google: false,
  },
  errors: [] as string[],
};

// ─── Formatters ────────────────────────────────────────────────────────────
function currency(value: number): string {
  if (value >= 1000) return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function pct(value: number | null): string {
  if (value === null) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${(value * 100).toFixed(0)}%`;
}

function statusColor(status: string): string {
  if (status === 'healthy') return C.lime;
  if (status === 'warning') return C.amber;
  if (status === 'danger') return C.red;
  return C.subtle;
}

function formatDate(value: string): string {
  if (!value) return 'pending';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function categoryForService(service: string): string {
  return (
    serviceCategories.find((category) =>
      category.keywords.some((keyword) => service.toLowerCase().includes(keyword.toLowerCase())),
    )?.label ?? 'Other AWS'
  );
}

function blendTimePoints(
  points: Array<Array<{ date?: string; month?: string; start?: string; spend: number }>>,
) {
  const totals = new Map<
    string,
    { date?: string; month?: string; start?: string; spend: number }
  >();
  points.flat().forEach((point) => {
    const key = point.start ?? point.date ?? point.month ?? '';
    if (!key) return;
    const existing = totals.get(key);
    totals.set(key, {
      date: point.date ?? existing?.date,
      month: point.month ?? existing?.month,
      start: point.start ?? existing?.start,
      spend: (existing?.spend ?? 0) + point.spend,
    });
  });
  return Array.from(totals.values()).sort((a, b) =>
    (a.start ?? a.date ?? a.month ?? '').localeCompare(b.start ?? b.date ?? b.month ?? ''),
  );
}

function deltaPct(current: number, previous: number): number | null {
  return previous > 0 ? (current - previous) / previous : null;
}

// ─── Shared primitives ─────────────────────────────────────────────────────
const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({
  children,
  style,
}) => (
  <div
    style={{
      background: C.card,
      backgroundImage: 'var(--tx-surface-light)',
      boxShadow: 'var(--tx-shadow)',
      borderRadius: '24px',
      padding: 20,
      fontFamily: font,
      ...style,
    }}>
    {children}
  </div>
);

/** The app's legend mark: a 2px hairline in the data colour. Colour lives here, never in the figure. */
const Tick: React.FC<{ color: string; style?: React.CSSProperties }> = ({ color, style }) => (
  <span
    aria-hidden
    style={{
      display: 'inline-block',
      width: 2,
      height: 12,
      borderRadius: 1,
      background: color,
      flexShrink: 0,
      ...style,
    }}
  />
);

/** A share, as segments on one 2px hairline track — the glanceable visual under every figure. */
const ShareStrip: React.FC<{ segments: { share: number; color: string }[] }> = ({ segments }) => (
  <div
    style={{
      display: 'flex',
      gap: 2,
      height: 2,
      borderRadius: 1,
      overflow: 'hidden',
      background: 'var(--tx-line)',
    }}>
    {segments.map((seg, i) => (
      <span
        key={i}
        style={{
          width: `${Math.min(1, Math.max(0, seg.share)) * 100}%`,
          background: seg.color,
          borderRadius: 1,
        }}
      />
    ))}
  </div>
);

const SectionHeader: React.FC<{
  eyebrow?: string;
  title: string;
  detail?: string;
  right?: React.ReactNode;
}> = ({ eyebrow, title, detail, right }) => (
  <div style={{ marginBottom: 16 }}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 20,
        alignItems: 'baseline',
      }}>
      <div style={{ color: C.text, fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em' }}>
        {title}
      </div>
      {right ??
        (eyebrow && (
          <span style={{ fontSize: 11, fontWeight: 500, color: C.muted, whiteSpace: 'nowrap' }}>
            {eyebrow}
          </span>
        ))}
    </div>
    {detail && (
      <div
        style={{
          color: C.muted,
          fontSize: 12,
          fontWeight: 500,
          marginTop: 4,
          lineHeight: 1.5,
          maxWidth: 520,
        }}>
        {detail}
      </div>
    )}
  </div>
);

/** One metric as a hairline-separated row inside the sheet: label + light ink figure, its meta, and a share strip. */
const MetricRow: React.FC<{
  label: string;
  value: string;
  sub?: string;
  share?: number | null;
  color?: string;
  /** a shaped visual (bars, dots) that stands in for the share strip when the figure has more shape than a share */
  visual?: React.ReactNode;
  loading?: boolean;
  first?: boolean;
  last?: boolean;
}> = ({ label, value, sub, share, color = C.blue, visual, loading, first, last }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      paddingTop: first ? 0 : 14,
      paddingBottom: last ? 0 : 14,
      borderTop: first ? 'none' : `1px solid ${C.border}`,
      minWidth: 0,
    }}>
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 12,
      }}>
      <span style={{ fontSize: 13, fontWeight: 500, color: C.text, whiteSpace: 'nowrap' }}>
        {label}
      </span>
      {loading ? (
        <div style={{ width: 80 }}>
          <Skeleton height={22} />
        </div>
      ) : (
        <span
          style={{
            fontSize: 24,
            fontWeight: 200,
            letterSpacing: '-0.05em',
            lineHeight: 1,
            color: C.text,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}>
          {value}
        </span>
      )}
    </div>
    {sub && !loading && (
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: C.muted,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
        {sub}
      </div>
    )}
    {visual && !loading ? (
      visual
    ) : (
      <ShareStrip segments={!loading && share != null ? [{ share, color }] : []} />
    )}
  </div>
);

const Skeleton: React.FC<{ height?: number; radius?: number }> = ({ height = 18, radius = 6 }) => (
  <div
    style={{
      width: '100%',
      height,
      borderRadius: radius,
      background: 'var(--tx-fill)',
      animation: 'infraPulse 1.5s ease-in-out infinite',
    }}
  />
);

/** Tick + label for status readouts that sit straight on the chalk ground (not inside a sheet), so they take the
 * ground ink — the theme's ink is light at night and would vanish on chalk. */
const StatusTick: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <Tick color={color} />
    <span style={{ fontFamily: font, fontSize: 12, fontWeight: 500, color: GROUND_INK }}>
      {children}
    </span>
  </div>
);

// Frosted-glass tooltip (the app's tooltip spec): fixed light ink so it reads on any theme.
const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string; payload?: { start?: string } }>;
  label?: string;
}> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: 'rgba(38,38,38,0.6)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderRadius: 12,
        color: '#FFFFFF',
        fontFamily: font,
        fontSize: 12,
        fontWeight: 500,
        padding: '10px 12px',
      }}>
      <div style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500, marginBottom: 6 }}>
        {label}
      </div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: '#FFFFFF', fontVariantNumeric: 'tabular-nums' }}>
          {p.name}: <span style={{ fontWeight: 600 }}>{currency(Number(p.value))}</span>
        </div>
      ))}
    </div>
  );
};

const HeroSpendTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string; payload?: { start?: string } }>;
  label?: string;
}> = ({ active, payload, label }) => {
  const start = payload?.[0]?.payload?.start;
  const fullDate = start
    ? new Date(`${start}T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      })
    : label;

  return <CustomTooltip active={active} payload={payload} label={fullDate} />;
};

const PostureRow: React.FC<{ item: AdminInfraInsightsPosture; isLast?: boolean }> = ({
  item,
  isLast = true,
}) => (
  <div
    style={{
      padding: '12px 0',
      borderBottom: isLast ? 'none' : `1px solid ${C.border}`,
      display: 'grid',
      gridTemplateColumns: '2px minmax(0, 1fr) auto',
      columnGap: 10,
      alignItems: 'baseline',
    }}>
    <Tick color={statusColor(item.status)} style={{ alignSelf: 'center' }} />
    <div style={{ minWidth: 0 }}>
      <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{item.label}</div>
      <div style={{ color: C.muted, fontSize: 12, fontWeight: 500, marginTop: 3 }}>
        {item.detail}
      </div>
    </div>
    <div
      style={{
        color: C.text,
        fontSize: 12,
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}>
      {item.value}
    </div>
  </div>
);

// The legend treatment: a 2px tick in the severity colour leads the title; the detail sits under it in muted.
const WatchItem: React.FC<{ item: AdminInfraInsightsWatchItem; isLast?: boolean }> = ({
  item,
  isLast = true,
}) => (
  <div
    style={{
      padding: '12px 0',
      borderBottom: isLast ? 'none' : `1px solid ${C.border}`,
    }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Tick color={statusColor(item.severity)} />
      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, minWidth: 0 }}>{item.title}</div>
    </div>
    <div
      style={{
        color: C.muted,
        fontSize: 12,
        fontWeight: 500,
        marginTop: 4,
        marginLeft: 10,
        lineHeight: 1.45,
      }}>
      {item.detail}
    </div>
  </div>
);

const EmptyState: React.FC<{ title: string; detail: string }> = ({ title, detail }) => (
  <div
    style={{
      minHeight: 160,
      background: 'var(--tx-fill)',
      borderRadius: 16,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: 24,
    }}>
    <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{title}</div>
    <div
      style={{
        color: C.muted,
        fontSize: 12,
        fontWeight: 500,
        marginTop: 6,
        maxWidth: 400,
        lineHeight: 1.5,
      }}>
      {detail}
    </div>
  </div>
);

/** Narrative stat — the page's "business story" device, as a hairline-topped column inside the sheet (no inner
 * box). The colour lives on the tick and the share strip; the figure stays ink. */
const StoryCard: React.FC<{
  title: string;
  detail: string;
  accent: string;
  value?: string;
  share?: number | null;
}> = ({ title, detail, accent, value, share }) => (
  <div
    style={{
      borderTop: `1px solid ${C.border}`,
      paddingTop: 14,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      minWidth: 0,
    }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Tick color={accent} />
      <span style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{title}</span>
    </div>
    {value && (
      <div
        style={{
          color: C.text,
          fontSize: 34,
          fontWeight: 200,
          letterSpacing: '-0.05em',
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
        }}>
        {value}
      </div>
    )}
    {share != null && <ShareStrip segments={[{ share, color: accent }]} />}
    <div style={{ color: C.muted, fontSize: 12, fontWeight: 500, lineHeight: 1.5 }}>{detail}</div>
  </div>
);

/** Ranked, single-accent hairline list. Rank communicates via opacity, not a rainbow of hues per row. */
const RankedBars: React.FC<{
  rows: { label: string; value: number }[];
  format: (n: number) => string;
  max: number;
}> = ({ rows, format, max }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    {rows.map((row, i) => (
      <div key={row.label}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            color: C.muted,
            fontSize: 12,
            fontWeight: 500,
            marginBottom: 6,
          }}>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.label}
          </span>
          <span style={{ color: C.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {format(row.value)}
          </span>
        </div>
        <div style={{ height: 2, borderRadius: 1, background: C.border, overflow: 'hidden' }}>
          <div
            style={{
              width: `${Math.max(2, (row.value / Math.max(max, 1)) * 100)}%`,
              height: '100%',
              borderRadius: 1,
              background: C.muted,
              opacity: Math.max(0.35, 1 - i * 0.15),
            }}
          />
        </div>
      </div>
    ))}
  </div>
);

const LEDGER_GRID =
  'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 0.8fr) minmax(0, 1.4fr) minmax(0, 0.9fr)';

const ServiceLedger: React.FC<{ rows: AdminInfraInsightsServiceSpend[] }> = ({ rows }) => (
  <Card>
    <SectionHeader
      title="AWS service ledger"
      detail="Top current-month services, ordered by spend."
      right={
        rows.length ? (
          <span style={{ color: C.muted, fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' }}>
            <span style={{ color: C.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {rows.length}
            </span>{' '}
            service{rows.length === 1 ? '' : 's'}
          </span>
        ) : undefined
      }
    />
    {!rows.length ? (
      <EmptyState title="Ledger unavailable" detail="No service-level AWS data returned yet." />
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: LEDGER_GRID,
            gap: 16,
            padding: '0 0 10px',
            borderBottom: `1px solid ${C.border}`,
          }}>
          {['Service', 'Category', 'MTD spend', 'Share of AWS', 'Signal'].map((h) => (
            <div
              key={h}
              style={{
                fontSize: 11,
                fontWeight: 500,
                color: C.muted,
              }}>
              {h}
            </div>
          ))}
        </div>
        {rows.map((row, i) => {
          const concentrated = row.pct > 0.35;
          return (
            <div
              key={row.service}
              style={{
                display: 'grid',
                gridTemplateColumns: LEDGER_GRID,
                gap: 16,
                padding: '12px 0',
                alignItems: 'center',
                borderBottom: i === rows.length - 1 ? 'none' : `1px solid ${C.border}`,
              }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{row.service}</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: C.muted }}>
                {categoryForService(row.service)}
              </div>
              <div style={{ fontSize: 13, color: C.text, fontVariantNumeric: 'tabular-nums' }}>
                {currency(row.spend)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    flex: 1,
                    height: 2,
                    borderRadius: 1,
                    background: C.border,
                    overflow: 'hidden',
                  }}>
                  <div
                    style={{
                      width: `${Math.max(2, row.pct * 100)}%`,
                      height: '100%',
                      borderRadius: 1,
                      background: C.muted,
                      opacity: Math.max(0.35, 1 - i * 0.12),
                    }}
                  />
                </div>
                <span
                  style={{
                    width: 36,
                    textAlign: 'right',
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.text,
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                  {(row.pct * 100).toFixed(0)}%
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Tick color={concentrated ? C.amber : C.monitor} />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: concentrated ? C.text : C.muted,
                  }}>
                  {concentrated ? 'Concentrated' : 'Normal'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </Card>
);

export const InfracostsTab: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const { insights, isLoading, error, isFetching } = useAdminInfraInsights();
  // Defensive against an API response that predates the claudeSpend field
  // (e.g. mid-deploy, or a stale cached response) — fall back per-field
  // rather than crashing on `data.claudeSpend.mtd` when claudeSpend is absent.
  const data = insights
    ? {
        ...fallback,
        ...insights,
        claudeSpend: insights.claudeSpend ?? fallback.claudeSpend,
        googleSpend: insights.googleSpend ?? fallback.googleSpend,
        rawAvailability: { ...fallback.rawAvailability, ...insights.rawAvailability },
      }
    : fallback;
  const blendedMonthly = useMemo(
    () => blendTimePoints([data.spend.monthly, data.claudeSpend.monthly, data.googleSpend.monthly]),
    [data.spend.monthly, data.claudeSpend.monthly, data.googleSpend.monthly],
  );
  const blendedDaily = useMemo(
    () => blendTimePoints([data.spend.daily, data.claudeSpend.daily, data.googleSpend.daily]),
    [data.spend.daily, data.claudeSpend.daily, data.googleSpend.daily],
  );
  const blendedMtd = data.spend.mtd + data.claudeSpend.mtd + data.googleSpend.mtd;
  const blendedPreviousMonth =
    data.spend.previousMonth + data.claudeSpend.previousMonth + data.googleSpend.previousMonth;
  const blendedMonthDeltaPct = deltaPct(blendedMtd, blendedPreviousMonth);
  const blendedSevenDay = blendedDaily.slice(-7).reduce((sum, row) => sum + row.spend, 0);
  const blendedPreviousSevenDay = blendedDaily
    .slice(-14, -7)
    .reduce((sum, row) => sum + row.spend, 0);
  const blendedSevenDayDeltaPct = deltaPct(blendedSevenDay, blendedPreviousSevenDay);
  const blendedServices = useMemo(() => {
    const rows = [
      ...data.spend.topServices.map((row) => ({ ...row, service: `AWS · ${row.service}` })),
      ...data.claudeSpend.topModels.map((row) => ({ ...row, service: `Claude · ${row.service}` })),
      ...data.googleSpend.topSkus.map((row) => ({
        ...row,
        service: `Google Maps · ${row.service}`,
      })),
    ].filter((row) => row.spend > 0);
    const total = rows.reduce((sum, row) => sum + row.spend, 0);
    return rows
      .map((row) => ({ ...row, pct: total > 0 ? row.spend / total : 0 }))
      .sort((a, b) => b.spend - a.spend)
      .slice(0, 10);
  }, [data.spend.topServices, data.claudeSpend.topModels, data.googleSpend.topSkus]);
  const topService = blendedServices[0];
  const hasSpend = blendedMonthly.length > 0 || blendedServices.length > 0;

  const categorySpend = useMemo(() => {
    const totals = blendedServices.reduce<Record<string, number>>((acc, service) => {
      const label = categoryForService(service.service);
      acc[label] = (acc[label] ?? 0) + service.spend;
      return acc;
    }, {});
    return Object.entries(totals)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }, [blendedServices]);

  const activeWarnings = data.watchlist.filter((item) => item.severity !== 'healthy').length;
  const coverageTotal = Object.keys(data.rawAvailability).length;
  const coverageCount = Object.values(data.rawAvailability).filter(Boolean).length;
  const dataFreshness = data.generatedAt ? formatDate(data.generatedAt) : 'pending';
  const topClaudeModel = data.claudeSpend.topModels[0];
  const topGoogleSku = data.googleSpend.topSkus[0];
  // Colour carries meaning only on ticks and hairlines: caution when burn is up >15% or one line dominates.
  const burnTone =
    blendedMonthDeltaPct !== null && blendedMonthDeltaPct > 0.15 ? C.amber : C.lime;
  const concentrationTone = topService && topService.pct > 0.35 ? C.amber : C.blue;
  const coverageTone = coverageCount === coverageTotal ? C.lime : C.amber;
  const showSkeletons = isLoading && !insights;

  return (
    <div
      style={{
        fontFamily: font,
        height: '100%',
        overflow: 'auto',
        padding: '20px 0',
        boxSizing: 'border-box',
        scrollbarWidth: 'none',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <style>{`
        @keyframes infraPulse { 0%, 100% { opacity: 1 } 50% { opacity: 0.4 } }
        /* One consistent rhythm: 20px between every top-level sheet/row and between
           columns composed inside a sheet. */
        .infra-shell { display: flex; flex-direction: column; gap: 20px; flex: 1; min-height: 0; }
        /* An overview tab fills the laptop's view height: its rows share the room below the header (the hero row
           takes more), and each row's sheets stretch to the row, so nothing ends in an empty band */
        .infra-fill { display: flex; flex-direction: column; gap: 20px; }
        @media (min-width: 1101px) and (min-height: 700px) {
          .infra-fill { flex: 1; min-height: 0; display: grid; grid-template-rows: minmax(0, 1.35fr) minmax(0, 1fr); }
          .infra-fill > .infra-two-col { min-height: 0; }
          .infra-fill > .infra-two-col > * { min-height: 0; }
        }
        .infra-header { display: flex; justify-content: space-between; align-items: center; gap: 20px; flex-wrap: wrap; }
        /* Same grid for every 2-column row on the page, so left/right edges line up
           top to bottom instead of each row inventing its own ratio. */
        .infra-two-col { display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr); gap: 20px; }
        .infra-three-col { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; }
        @media (max-width: 1100px) {
          .infra-two-col, .infra-three-col { grid-template-columns: minmax(0, 1fr); }
          .infra-header { align-items: flex-start; flex-direction: column; }
        }
      `}</style>

      <div className="infra-shell">
        {/* Tabs left, live status right. The status sits straight on the chalk ground, so it
            takes the ground ink (the theme ink is light at night and vanishes on chalk). */}
        <div className="infra-header">
          <Segmented
            label="Infra command center sections"
            size="sm"
            value={activeTab}
            onChange={setActiveTab}
            options={tabOptions}
          />
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusTick color={error ? C.red : C.lime}>
              {error ? 'Data unavailable' : isLoading ? 'Loading' : 'Live snapshot'}
            </StatusTick>
            <StatusTick color={activeWarnings ? C.amber : C.lime}>
              {activeWarnings > 0
                ? `${activeWarnings} warning${activeWarnings === 1 ? '' : 's'}`
                : 'All clear'}
            </StatusTick>
            {!showSkeletons && (
              <span style={{ color: GROUND_MUTED, fontSize: 12, fontWeight: 500 }}>
                Updated {dataFreshness}
                {isFetching ? ' · refreshing…' : ''}
              </span>
            )}
          </div>
        </div>

        {activeTab === 'Overview' && (
          <div className="infra-fill">
            <div className="infra-two-col">
              {/* Hero — the one number that matters most, framed as a sentence, not a box. */}
              <Card
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 10,
                  }}>
                  <span
                    style={{
                      fontSize: 17,
                      fontWeight: 400,
                      letterSpacing: '-0.015em',
                      color: C.text,
                    }}>
                    Month-to-date operating burn
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 500, color: C.muted, whiteSpace: 'nowrap' }}>
                    AWS · Claude · Google
                  </span>
                </div>
                {showSkeletons ? (
                  <Skeleton height={56} radius={10} />
                ) : (
                  <>
                    <div
                      style={{
                        fontSize: 46,
                        fontWeight: 200,
                        color: C.text,
                        letterSpacing: '-0.05em',
                        lineHeight: 1,
                        fontVariantNumeric: 'tabular-nums',
                        margin: '14px 0 10px',
                      }}>
                      {currency(blendedMtd)}
                    </div>
                    {blendedMonthly.length > 1 && (
                      <div style={{ flex: 1, minHeight: 100, margin: '8px 0' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart
                            data={blendedMonthly}
                            margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                            <Tooltip content={<HeroSpendTooltip />} cursor={false} />
                            <Area
                              type="monotone"
                              dataKey="spend"
                              {...lineSeries(C.muted)}
                              fill={C.muted}
                              fillOpacity={0.06}
                              dot={nowDot(blendedMonthly.length - 1)}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </>
                )}
                {!showSkeletons && (
                  <div style={{ color: C.muted, fontSize: 13, fontWeight: 500, lineHeight: 1.6 }}>
                    {blendedMonthDeltaPct === null ? (
                      'No prior-month baseline yet to compare against.'
                    ) : (
                      <>
                        That's{' '}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <Tick color={burnTone} />
                          <span style={{ color: C.text, fontWeight: 600 }}>
                            {pct(blendedMonthDeltaPct)}
                          </span>
                        </span>{' '}
                        versus last month, and{' '}
                        <span style={{ color: C.text, fontWeight: 600 }}>
                          {currency(blendedSevenDay)}
                        </span>{' '}
                        over the last 7 days.
                      </>
                    )}
                  </div>
                )}
              </Card>

              {/* Supporting metrics as hairline-separated rows in one sheet — no tiles inside the card. */}
              <Card style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <MetricRow
                  first
                  label="7-day burn"
                  value={currency(blendedSevenDay)}
                  sub={`${pct(blendedSevenDayDeltaPct)} vs previous 7 days`}
                  share={
                    blendedSevenDay + blendedPreviousSevenDay > 0
                      ? blendedSevenDay / (blendedSevenDay + blendedPreviousSevenDay)
                      : null
                  }
                  color={
                    blendedSevenDayDeltaPct !== null && blendedSevenDayDeltaPct > 0.15
                      ? C.amber
                      : C.blue
                  }
                  visual={
                    blendedDaily.length > 1 ? (
                      <Bars
                        height={24}
                        gap={2}
                        now={Math.min(blendedDaily.length, 14) - 1}
                        format={currency}
                        data={blendedDaily.slice(-14).map((row, i, rows) => ({
                          label: row.date ?? row.start ?? '',
                          value: row.spend,
                          // the prior week sits fainter so the two weeks compare at a glance
                          color: i < rows.length - 7 ? PRIOR_INK : undefined,
                        }))}
                      />
                    ) : undefined
                  }
                  loading={showSkeletons}
                />
                <MetricRow
                  label="Largest cost line"
                  value={topService ? currency(topService.spend) : '—'}
                  sub={
                    topService
                      ? `${topService.service} · ${(topService.pct * 100).toFixed(
                          0,
                        )}% of tracked spend`
                      : 'No service data yet'
                  }
                  share={topService ? topService.pct : null}
                  color={concentrationTone}
                  loading={showSkeletons}
                />
                <MetricRow
                  label="Claude API (MTD)"
                  value={currency(data.claudeSpend.mtd)}
                  sub={
                    topClaudeModel
                      ? `${topClaudeModel.service} · ${(topClaudeModel.pct * 100).toFixed(
                          0,
                        )}% of Claude spend`
                      : 'No Claude spend data yet'
                  }
                  share={blendedMtd > 0 ? data.claudeSpend.mtd / blendedMtd : null}
                  loading={showSkeletons}
                />
                <MetricRow
                  label="Google Maps API (MTD)"
                  value={currency(data.googleSpend.mtd)}
                  sub={
                    !data.rawAvailability.google
                      ? 'BigQuery billing export unavailable'
                      : topGoogleSku
                      ? `${topGoogleSku.service} · ${(topGoogleSku.pct * 100).toFixed(
                          0,
                        )}% of Google spend`
                      : data.googleSpend.monthly.length
                      ? 'Connected · no current-month SKU spend yet'
                      : 'Connected · no Google Maps spend rows yet'
                  }
                  share={blendedMtd > 0 ? data.googleSpend.mtd / blendedMtd : null}
                  loading={showSkeletons}
                />
                <MetricRow
                  last
                  label="Data coverage"
                  value={`${coverageCount}/${coverageTotal}`}
                  sub="AWS · SES · Cognito · Claude · Google"
                  share={coverageTotal > 0 ? coverageCount / coverageTotal : null}
                  color={coverageTone}
                  visual={
                    <Dots
                      items={Object.entries(data.rawAvailability).map(([key, on]) => ({
                        key,
                        on: Boolean(on),
                        label: `${coverageLabels[key] ?? key} · ${on ? 'connected' : 'unavailable'}`,
                      }))}
                    />
                  }
                  loading={showSkeletons}
                />
              </Card>
            </div>

            <div className="infra-two-col">
              <Card>
                <SectionHeader
                  eyebrow="Executive read"
                  title="What changed, what matters, what to watch."
                  detail="The fast operating story: spend trend, service concentration, and whether the app's own visibility into its infra is complete."
                />
                <div className="infra-three-col">
                  <StoryCard
                    title="Burn discipline"
                    value={pct(blendedMonthDeltaPct)}
                    accent={burnTone}
                    share={
                      blendedPreviousMonth > 0 ? blendedMtd / blendedPreviousMonth : null
                    }
                    detail="Month-to-date movement across AWS, Claude, and Google versus the prior month."
                  />
                  <StoryCard
                    title="Service concentration"
                    value={topService ? `${(topService.pct * 100).toFixed(0)}%` : '—'}
                    accent={concentrationTone}
                    share={topService ? topService.pct : null}
                    detail={
                      topService
                        ? `${topService.service} is the largest current-month driver.`
                        : 'No service spend returned yet.'
                    }
                  />
                  <StoryCard
                    title="Operational readiness"
                    value={`${coverageCount}/${coverageTotal}`}
                    accent={coverageTone}
                    share={coverageTotal > 0 ? coverageCount / coverageTotal : null}
                    detail="Whether AWS billing, Claude API cost, Google billing, email, and Cognito posture data are visible."
                  />
                </div>
              </Card>

              <Card>
                <SectionHeader
                  title="Watchlist"
                  detail="Automatically generated cost and configuration signals."
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {data.watchlist.length ? (
                    data.watchlist.map((item, i) => (
                      <WatchItem
                        key={item.title}
                        item={item}
                        isLast={i === data.watchlist.length - 1}
                      />
                    ))
                  ) : (
                    <WatchItem
                      item={{
                        severity: 'healthy',
                        title: 'No active infra warnings',
                        detail: hasSpend
                          ? 'Spend and core auth/email posture are within expected bounds.'
                          : 'Warnings will populate once live AWS data is available.',
                      }}
                    />
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'Costs' && (
          <>
            <div className="infra-two-col">
              <Card>
                <SectionHeader
                  title="Spend trend"
                  detail="Rolling monthly tracked spend across AWS, Claude, and Google."
                />
                {blendedMonthly.length ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart
                      data={blendedMonthly}
                      margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                      <CartesianGrid {...quietGrid} />
                      <XAxis dataKey="month" {...quietAxis} minTickGap={12} />
                      <YAxis hide />
                      <Tooltip content={<CustomTooltip />} cursor={false} />
                      <Area
                        type="monotone"
                        dataKey="spend"
                        name="Spend"
                        {...lineSeries(C.muted)}
                        fill={C.muted}
                        fillOpacity={0.06}
                        dot={nowDot(blendedMonthly.length - 1)}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState
                    title="No cost data yet"
                    detail="Tracked provider spend will appear here once AWS, Claude, or Google billing data is available."
                  />
                )}
              </Card>

              <Card>
                <SectionHeader
                  title="Daily burn"
                  detail="Last 30 days. Useful for catching deployment or usage spikes."
                />
                {blendedDaily.length ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart
                      data={blendedDaily}
                      margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                      <CartesianGrid {...quietGrid} />
                      <XAxis dataKey="date" hide />
                      <YAxis hide />
                      <Tooltip content={<CustomTooltip />} cursor={false} />
                      <Line
                        type="monotone"
                        dataKey="spend"
                        name="Daily spend"
                        {...lineSeries(C.muted)}
                        dot={nowDot(blendedDaily.length - 1)}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState
                    title="No daily spend yet"
                    detail="Daily Cost Explorer data will appear here."
                  />
                )}
              </Card>
            </div>

            <div className="infra-two-col">
              <Card>
                <SectionHeader
                  title="Service concentration"
                  detail="Current-month spend by provider line item, ranked."
                />
                {blendedServices.length ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={blendedServices}
                      layout="vertical"
                      margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                      <XAxis type="number" hide />
                      {/* the category axis line is the bars' baseline */}
                      <YAxis
                        dataKey="service"
                        type="category"
                        width={185}
                        interval={0}
                        tick={<ServiceAxisTick />}
                        axisLine={{ stroke: C.border }}
                        tickLine={false}
                      />
                      <Tooltip content={<CustomTooltip />} cursor={false} />
                      <Bar
                        dataKey="spend"
                        name="Spend"
                        barSize={6}
                        radius={2}
                        fill={SERIES_INK}
                        background={false}
                        isAnimationActive={false}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState
                    title="No service spend yet"
                    detail="Grouped Cost Explorer data will surface the fastest way to spot what is driving burn."
                  />
                )}
              </Card>

              <Card>
                <SectionHeader
                  title="Spend by product surface"
                  detail="Hauser-flavored grouping across cloud, AI, and maps/vendor-discovery costs."
                />
                {categorySpend.length ? (
                  <RankedBars
                    rows={categorySpend}
                    format={currency}
                    max={categorySpend[0]?.value ?? 1}
                  />
                ) : (
                  <EmptyState
                    title="No category spend yet"
                    detail="Service-level costs will be grouped here."
                  />
                )}
              </Card>
            </div>

            <div className="infra-two-col">
              <Card>
                <SectionHeader
                  title="Claude API spend"
                  detail="Rolling monthly Anthropic cost, direct from the Usage & Cost Admin API — separate from AWS billing."
                />
                {data.claudeSpend.monthly.length ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart
                      data={data.claudeSpend.monthly}
                      margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                      <CartesianGrid {...quietGrid} />
                      <XAxis dataKey="month" {...quietAxis} minTickGap={12} />
                      <YAxis hide />
                      <Tooltip content={<CustomTooltip />} cursor={false} />
                      <Area
                        type="monotone"
                        dataKey="spend"
                        name="Spend"
                        {...lineSeries(C.muted)}
                        fill={C.muted}
                        fillOpacity={0.06}
                        dot={nowDot(data.claudeSpend.monthly.length - 1)}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState
                    title="No Claude cost data yet"
                    detail="Set the AnthropicAdminApiKey secret to an Admin API key (Console > Settings > Admin API keys) to enable this."
                  />
                )}
              </Card>

              <Card>
                <SectionHeader
                  title="Claude cost by model"
                  detail="Current-month spend, grouped by model."
                />
                {data.claudeSpend.topModels.length ? (
                  <RankedBars
                    rows={data.claudeSpend.topModels.map((row) => ({
                      label: row.service,
                      value: row.spend,
                    }))}
                    format={currency}
                    max={data.claudeSpend.topModels[0]?.spend ?? 1}
                  />
                ) : (
                  <EmptyState
                    title="No model-level spend yet"
                    detail="Per-model Claude cost will be ranked here once data is available."
                  />
                )}
              </Card>
            </div>

            <div className="infra-two-col">
              <Card>
                <SectionHeader
                  title="Google Maps Platform spend"
                  detail="Rolling monthly Google Maps and Places cost from BigQuery billing export."
                />
                {data.googleSpend.monthly.length ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart
                      data={data.googleSpend.monthly}
                      margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                      <CartesianGrid {...quietGrid} />
                      <XAxis dataKey="month" {...quietAxis} minTickGap={12} />
                      <YAxis hide />
                      <Tooltip content={<CustomTooltip />} cursor={false} />
                      <Area
                        type="monotone"
                        dataKey="spend"
                        name="Spend"
                        {...lineSeries(C.muted)}
                        fill={C.muted}
                        fillOpacity={0.06}
                        dot={nowDot(data.googleSpend.monthly.length - 1)}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState
                    title="No Google cost data yet"
                    detail="Waiting for Google Cloud Billing export to create the BigQuery billing table."
                  />
                )}
              </Card>

              <Card>
                <SectionHeader
                  title="Google cost by SKU"
                  detail="Current-month Maps and Places spend, grouped by SKU."
                />
                {data.googleSpend.topSkus.length ? (
                  <RankedBars
                    rows={data.googleSpend.topSkus.map((row) => ({
                      label: row.service,
                      value: row.spend,
                    }))}
                    format={currency}
                    max={data.googleSpend.topSkus[0]?.spend ?? 1}
                  />
                ) : (
                  <EmptyState
                    title="No Google SKU spend yet"
                    detail="SKU-level cost will appear once BigQuery billing export has rows."
                  />
                )}
              </Card>
            </div>

            <ServiceLedger rows={data.spend.topServices} />
          </>
        )}

        {activeTab === 'Usage' && (
          <Card>
            <SectionHeader
              eyebrow="Instrumentation plan"
              title="Usage should answer: what did customers actually do?"
              detail="This tab is intentionally structured before we add rollups, so the metrics become user stories instead of raw counters."
            />
            <div className="infra-three-col">
              <StoryCard
                title="Reports processed"
                accent={storyAccents[0]}
                detail="Inspection reports uploaded, classified, extracted, scored, and made useful."
              />
              <StoryCard
                title="Vendor workflows"
                accent={storyAccents[1]}
                detail="Searches, curated vendor lists, RFQs generated, and saved directory entries."
              />
              <StoryCard
                title="Advisor/client engagement"
                accent={storyAccents[2]}
                detail="Messages, claims reviewed, task activity, and account activation moments."
              />
            </div>
            <div style={{ marginTop: 20 }}>
              <EmptyState
                title="Next backend step: scheduled app rollups"
                detail={
                  'We should store daily counters for reports, uploads, jobs, users, properties, ' +
                  'vendor actions, messages, and RFQs. That avoids expensive table scans and gives us clean trends.'
                }
              />
            </div>
          </Card>
        )}

        {activeTab === 'Unit Economics' && (
          <Card>
            <SectionHeader
              eyebrow="Business model"
              title="Cost only matters when it is tied to value."
              detail="This is where AWS spend becomes cost per useful action: report, property, owner, advisor, RFQ, and completed workflow."
            />
            <div className="infra-three-col">
              <StoryCard
                title="Cost / report"
                value="Pending"
                accent={storyAccents[0]}
                detail="Needs report-count rollups and AI/document-processing cost attribution."
              />
              <StoryCard
                title="Cost / active property"
                value="Pending"
                accent={storyAccents[1]}
                detail="Needs daily active property and account snapshots."
              />
              <StoryCard
                title="Cost / activated workflow"
                value="Pending"
                accent={storyAccents[2]}
                detail="Needs product event definitions: claim, upload, vendor search, RFQ, repair plan."
              />
            </div>
          </Card>
        )}

        {activeTab === 'Health' && (
          <div className="infra-two-col">
            <Card>
              <SectionHeader
                title="Infra posture"
                detail="Things that affect production readiness, email delivery, and signup behavior."
              />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {data.posture.length ? (
                  data.posture.map((item, i) => (
                    <PostureRow
                      key={item.label}
                      item={item}
                      isLast={i === data.posture.length - 1}
                    />
                  ))
                ) : (
                  <EmptyState
                    title="Posture checks pending"
                    detail="SES and Cognito checks will appear here when the endpoint has permission."
                  />
                )}
              </div>
            </Card>

            <Card>
              <SectionHeader
                title="Partial data warnings"
                detail="If AWS, Claude, Google, SES, or Cognito denies a data source, we show the page anyway and make the blind spot obvious."
              />
              {data.errors.length ? (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {data.errors.map((item, index) => (
                    <WatchItem
                      key={`${item}-${index}`}
                      item={{
                        severity: 'warning',
                        title: `Data source ${index + 1}`,
                        detail: item,
                      }}
                      isLast={index === data.errors.length - 1}
                    />
                  ))}
                </div>
              ) : (
                <WatchItem
                  item={{
                    severity: 'healthy',
                    title: 'All configured data sources responded',
                    detail:
                      'Cost Explorer, SES, Cognito, Claude Cost API, and Google BigQuery billing export are available for this snapshot.',
                  }}
                />
              )}
            </Card>
          </div>
        )}

        {activeTab === 'Signals' && (
          <Card>
            <SectionHeader
              eyebrow="User stories"
              title="The page should eventually read like a business narrative."
              detail="These are the meaningful stories we should wire into metrics once we add event and daily rollup instrumentation."
            />
            <div className="infra-three-col">
              <StoryCard
                title="Self-serve owners reached value"
                accent={storyAccents[0]}
                detail="Created account, claimed/created home, uploaded report, viewed score, explored repair planning."
              />
              <StoryCard
                title="Advisors converted chaos into clarity"
                accent={storyAccents[1]}
                detail="Properties added, reports processed, issues reviewed, RFQs generated, clients engaged."
              />
              <StoryCard
                title="Vendor network became actionable"
                accent={storyAccents[2]}
                detail="Searches made, vendors curated, contacts saved, bid requests prepared."
              />
              <StoryCard
                title="Account and auth health"
                accent={storyAccents[0]}
                detail="Signups, SSO, verification, resets, failed auth attempts, and invitation flows."
              />
              <StoryCard
                title="AI pipeline reliability"
                accent={storyAccents[1]}
                detail="Queue time, extraction failures, retries, token usage, and report completion latency."
              />
              <StoryCard
                title="Cost efficiency over time"
                accent={storyAccents[2]}
                detail="Spend per value event should improve as workflows scale and processing gets cleaner."
              />
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
